import React from 'react';
import { Link } from 'react-router-dom';

const values = [
  {
    title: 'Trust and transparency',
    description:
      'We want customers and providers to understand how booking, deposits, visibility, and platform rules work without hidden complexity.',
  },
  {
    title: 'Local relevance',
    description:
      'HnB is being shaped as a Belfast-first platform focused on local density, neighbourhood discovery, and practical service access.',
  },
  {
    title: 'Responsible innovation',
    description:
      'AI should improve confidence and decision making, while remaining controlled, explainable, and properly integrated into the wider platform.',
  },
  {
    title: 'Provider fairness',
    description:
      'Independent providers need better digital visibility, clearer operational support, and pricing structures that feel commercially realistic.',
  },
  {
    title: 'Governance by design',
    description:
      'Role separation, auditability, dispute handling, and compliance-aware architecture are part of the platform direction from the beginning.',
  },
  {
    title: 'Long-term scalability',
    description:
      'The public website is only the front layer of a broader HnB journey toward an ERP-ready platform for beauty and personal services.',
  },
];

const objectives = [
  'Make it easier for customers to discover and compare trusted local providers.',
  'Connect AI-supported style exploration to practical booking decisions.',
  'Help providers market their services, portfolios, and specialist work more effectively.',
  'Reduce friction around bookings, deposits, payments, reminders, and confirmations.',
  'Build a foundation for future provider operations, reporting, and oversight.',
  'Create a prototype strong enough for showcase, learning, and future expansion.',
];

const teamGroups = [
  {
    title: 'Platform leadership',
    description:
      'The HnB direction combines product thinking, commercial planning, governance, and long-term platform architecture.',
  },
  {
    title: 'Operations and oversight',
    description:
      'Future HnB staff roles include business development, audit, support, finance, and grievance or dispute-handling functions.',
  },
  {
    title: 'Product and technology',
    description:
      'The platform is being shaped through a React, Node, and Supabase stack with future AI integration through controlled ERP-facing contracts.',
  },
];

export default function AboutPage() {
  return (
    <main
      style={{
        background: '#f8fafc',
        color: '#0f172a',
        minHeight: '100vh',
      }}
    >
      <section
        style={{
          padding: '72px 24px 56px',
          borderBottom: '1px solid #e2e8f0',
          background: '#ffffff',
        }}
      >
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          <p
            style={{
              margin: 0,
              color: '#4f46e5',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontSize: '0.9rem',
            }}
          >
            About HnB
          </p>

          <h1
            style={{
              margin: 0,
              fontSize: '3rem',
              lineHeight: 1.08,
              maxWidth: '12ch',
            }}
          >
            Building a trusted beauty-services platform for local discovery and growth
          </h1>

          <p
            style={{
              margin: 0,
              color: '#475569',
              fontSize: '1.08rem',
              lineHeight: 1.8,
              maxWidth: '72ch',
            }}
          >
            Handsome & Beautiful is being developed as a Belfast-first, two-sided
            digital platform for local personal services, combining discovery,
            comparison, booking, payments, provider visibility, and future
            AI-supported style try-on in one coherent experience.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              paddingTop: '6px',
            }}
          >
            <Link
              to="/services"
              style={{
                textDecoration: 'none',
                background: '#111827',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
              }}
            >
              Explore services
            </Link>

            <Link
              to="/contact"
              style={{
                textDecoration: 'none',
                background: '#ffffff',
                color: '#111827',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid #d1d5db',
              }}
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '18px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '24px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px 0',
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.8rem',
              }}
            >
              Vision
            </p>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.4rem' }}>
              A more trusted and intelligent local services journey
            </h2>
            <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
              Our vision is to make beauty and personal care discovery more trusted,
              more transparent, and more useful for both customers and independent
              providers through a platform that connects digital confidence with
              real-world service delivery.
            </p>
          </div>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '24px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px 0',
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.8rem',
              }}
            >
              Mission
            </p>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.4rem' }}>
              Link discovery, AI, booking, and provider growth in one platform
            </h2>
            <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
              Our mission is to build an integrated customer and provider experience
              where local search, future AI try-on, booking, payments, and provider
              operations can work together instead of remaining fragmented.
            </p>
          </div>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '20px',
              padding: '24px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px 0',
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.8rem',
              }}
            >
              Pledge
            </p>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.4rem' }}>
              Build with fairness, clarity, and operational discipline
            </h2>
            <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.75 }}>
              We aim to design HnB with customer trust, provider fairness, role
              clarity, and future governance in mind, so the product grows on a
              credible operational foundation rather than on short-term convenience.
            </p>
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '56px 24px',
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 340px) 1fr',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Objectives
            </p>
            <h2 style={{ margin: 0, fontSize: '2rem', lineHeight: 1.15 }}>
              What HnB is trying to achieve
            </h2>
            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.75 }}>
              These objectives follow the business and platform direction captured
              in your earlier HnB planning documents.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: '12px',
            }}
          >
            {objectives.map((item) => (
              <div
                key={item}
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  padding: '16px 18px',
                }}
              >
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    marginTop: '7px',
                    borderRadius: '999px',
                    background: '#4f46e5',
                    flexShrink: 0,
                  }}
                />
                <p style={{ margin: 0, color: '#334155', lineHeight: 1.7 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Values
            </p>
            <h2 style={{ margin: 0, fontSize: '2rem', lineHeight: 1.15 }}>
              Principles shaping the platform
            </h2>
            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.75, maxWidth: '72ch' }}>
              HnB is being planned as more than a booking front end, so these values
              need to guide both the public website and the deeper platform design.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
            }}
          >
            {values.map((value) => (
              <div
                key={value.title}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '18px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.08rem', color: '#111827' }}>
                  {value.title}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '56px 24px',
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '18px',
          }}
        >
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e5e7eb',
              borderRadius: '18px',
              padding: '22px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px 0',
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.8rem',
              }}
            >
              Team
            </p>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.5rem' }}>
              A platform team, not a single-service business
            </h2>
            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.75 }}>
              HnB’s public team story should reflect platform building, operations,
              governance, and product development rather than presenting the brand
              as one salon or one studio.
            </p>
          </div>

          <div
            style={{
              background: '#0f172a',
              color: '#ffffff',
              borderRadius: '18px',
              padding: '22px',
            }}
          >
            <p
              style={{
                margin: '0 0 10px 0',
                color: '#a5b4fc',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.8rem',
              }}
            >
              Naming rule
            </p>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.5rem' }}>
              Staff and stylist are not the same
            </h2>
            <p style={{ margin: 0, color: '#cbd5e1', lineHeight: 1.75 }}>
              In HnB, staff refers to internal HnB personnel, while stylist refers
              to provider-side service professionals such as stylists, barbers, nail
              technicians, tattoo artists, or piercing artists.
            </p>
          </div>
        </div>

        <div
          style={{
            maxWidth: '1080px',
            margin: '24px auto 0',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '16px',
          }}
        >
          {teamGroups.map((group) => (
            <div
              key={group.title}
              style={{
                background: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '18px',
                padding: '20px',
              }}
            >
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.08rem' }}>
                {group.title}
              </h3>
              <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
                {group.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: '64px 24px 72px' }}>
        <div
          style={{
            maxWidth: '960px',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            alignItems: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              color: '#4f46e5',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontSize: '0.85rem',
            }}
          >
            Continue exploring
          </p>

          <h2 style={{ margin: 0, fontSize: '2.15rem', lineHeight: 1.15 }}>
            Move from the HnB story into the wider public experience
          </h2>

          <p
            style={{
              margin: 0,
              color: '#64748b',
              lineHeight: 1.8,
              maxWidth: '62ch',
            }}
          >
            Explore services, review platform materials, or contact HnB to follow
            the project as it develops further.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Link
              to="/services"
              style={{
                textDecoration: 'none',
                background: '#111827',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
              }}
            >
              Services
            </Link>

            <Link
              to="/downloads"
              style={{
                textDecoration: 'none',
                background: '#ffffff',
                color: '#111827',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid #d1d5db',
              }}
            >
              Downloads
            </Link>

            <Link
              to="/careers"
              style={{
                textDecoration: 'none',
                background: '#ffffff',
                color: '#111827',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid #d1d5db',
              }}
            >
              Careers
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}